import { useEffect, useState } from "react";
import { AlarmClock, BellRing } from "lucide-react";
import { api } from "../lib/api";

const TYPE_COLORS = {
  "USCIS Response Due": "bg-red-950/60 text-red-300",
  "Biometrics Appointment": "bg-teal-light text-teal",
  Interview: "bg-teal-light text-teal",
  "Court Hearing": "bg-red-950/60 text-red-300",
  "Passport Expiration": "bg-champagne-light text-champagne-dark",
  "EAD Expiration": "bg-champagne-light text-champagne-dark",
  "Visa Expiration": "bg-champagne-light text-champagne-dark",
  "Document Request Deadline": "bg-champagne-light text-champagne-dark",
  "Tax Deadline": "bg-champagne-light text-champagne-dark",
  General: "bg-sand text-mutedink",
};

export default function Deadlines() {
  const [deadlines, setDeadlines] = useState([]);
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/deadlines"), api.get("/notices")])
      .then(([d, n]) => { setDeadlines(d.data); setNotices(n.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = deadlines.filter((d) => d.date >= today);
  const past = deadlines.filter((d) => d.date < today);

  if (loading) {
    return <div className="py-16 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-teal border-t-transparent animate-spin" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="portal-deadlines-page">
      <div>
        <h1 className="text-2xl font-serif font-semibold text-navy tracking-tight">Deadlines &amp; Notices</h1>
        <p className="mt-1 text-sm text-mutedink">Important dates and official communications about your journey.</p>
      </div>

      <section data-testid="deadlines-section">
        <h2 className="text-base font-sans font-semibold text-navy mb-4 flex items-center gap-2"><AlarmClock className="h-4 w-4 text-champagne-dark" /> Upcoming deadlines</h2>
        {upcoming.length === 0 ? (
          <p className="text-sm text-mutedink bg-surface border border-dashed border-line rounded-2xl p-8 text-center">No upcoming deadlines. When your team adds important dates, they will appear here with reminders.</p>
        ) : (
          <div className="space-y-3">
            {upcoming.map((d) => {
              const days = Math.ceil((new Date(d.date + "T00:00:00") - new Date(today + "T00:00:00")) / 86400000);
              return (
                <div key={d.deadline_id} className="bg-surface border border-line rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`deadline-row-${d.deadline_id}`}>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-navy">{d.title}</p>
                    {d.notes && <p className="text-xs text-mutedink mt-1">{d.notes}</p>}
                  </div>
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full w-fit ${TYPE_COLORS[d.type] || TYPE_COLORS.General}`}>{d.type}</span>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-navy">{d.date}</p>
                    <p className={`text-xs ${days <= 7 ? "text-red-600 font-semibold" : "text-mutedink"}`}>{days === 0 ? "Today" : days === 1 ? "Tomorrow" : `in ${days} days`}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {past.length > 0 && (
          <details className="mt-4">
            <summary className="text-xs text-mutedink cursor-pointer hover:text-white" data-testid="past-deadlines-toggle">Show past dates ({past.length})</summary>
            <div className="mt-3 space-y-2">
              {past.map((d) => (
                <div key={d.deadline_id} className="bg-sand/50 rounded-lg px-4 py-3 text-sm text-mutedink flex justify-between">
                  <span>{d.title}</span><span>{d.date}</span>
                </div>
              ))}
            </div>
          </details>
        )}
      </section>

      <section data-testid="notices-section">
        <h2 className="text-base font-sans font-semibold text-navy mb-4 flex items-center gap-2"><BellRing className="h-4 w-4 text-teal" /> Notices</h2>
        {notices.length === 0 ? (
          <p className="text-sm text-mutedink bg-surface border border-dashed border-line rounded-2xl p-8 text-center">No notices posted yet.</p>
        ) : (
          <div className="space-y-3">
            {notices.map((n) => (
              <div key={n.notice_id} className="bg-surface border border-line rounded-xl p-5" data-testid={`notice-${n.notice_id}`}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <p className="text-sm font-semibold text-navy">{n.title}</p>
                  <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-teal-light text-teal">{n.type}</span>
                </div>
                <p className="text-sm text-mutedink leading-relaxed whitespace-pre-wrap">{n.body}</p>
                <p className="text-xs text-mutedink mt-3">{n.date}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
